import { z } from 'zod';
import asyncDebounce from 'packages/lib/utils/debounce';
import {
  getBaseURLForAPIBackend,
  getDefaultHeaders,
} from 'packages/lib/utils/fetch';
import { ResponseNotOk } from 'packages/lib/utils/error';
import { user } from 'packages/lib/auth';
import { User } from 'packages/lib/auth/data';
import { ENV } from 'packages/env';

// Zod contract for the alert notification payload
const alertContract = z.object({
  channelId: z.string({ required_error: 'channelId is required.' }).min(1),
  message: z.string({ required_error: 'message is required.' }).min(1),
  title: z.string().optional(),
  id: z.string(),
  user: z.string(),
  timestamp: z.string(),
});

type ErrorEntry = {
  url: string;
  status: number;
  message?: string;
  count: number;
};

// Using a Map to store errors, keyed by a combination of url and status
const allServiceErrorsMap = new Map<string, Map<string, ErrorEntry>>();

// A set to keep track of error keys that have already been reported in the current debounced cycle.
const reportedErrorKeys = new Set<string>();

const CHANNEL_ID = 'C0APRGXGN1G';
const DEBOUNCE_TIME_MS = 30000; // 30 seconds

/**
 * Internal function to process and send debounced error notifications.
 * This function is debounced to prevent a flood of notifications for repeated errors.
 * The back-end publishes the errors via slack to #at-error-watch.
 */
const processAllServiceErrors = asyncDebounce(async (userData: User) => {
  if (allServiceErrorsMap.size === 0) {
    return;
  }

  for (const [serviceName, errorsMap] of allServiceErrorsMap.entries()) {
    if (errorsMap.size === 0) {
      continue;
    }

    let newErrorsMessage = '';
    const newErrorKeysForHashing: string[] = [];
    for (const [, errorEntry] of errorsMap.entries()) {
      const { url, status, message, count } = errorEntry;
      const errorKey = `${serviceName}-${url}-${status}`;

      // Only include errors that haven't been reported in this cycle yet.
      if (!reportedErrorKeys.has(errorKey)) {
        newErrorsMessage += `API Error: URL: ${url}, Status: ${status}, Occurrences: ${count}${
          message ? `, Message: ${message}` : ''
        }\n`;
        reportedErrorKeys.add(errorKey);
        newErrorKeysForHashing.push(errorKey);
      }
    }

    if (!newErrorsMessage) continue;

    // Create a simple hash from the sorted error keys to generate a consistent ID
    const sortedErrorKeys = newErrorKeysForHashing.sort().join('|');
    let hash = 0;
    for (let i = 0; i < sortedErrorKeys.length; i++) {
      const char = sortedErrorKeys.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash |= 0; // Convert to 32bit integer
    }

    const fullNotificationMessage = `Error Report for Service: ${serviceName}\n\n${newErrorsMessage}`;
    try {
      const payload = alertContract.parse({
        channelId: CHANNEL_ID,
        message: fullNotificationMessage,
        title: `API Error Report for ${serviceName}`,
        id: hash.toString(),
        user: userData.email || 'unknown.user',
        timestamp: new Date().toISOString(),
      });

      if (ENV === 'production' || ENV === 'beta') {
        await fetch(
          `${getBaseURLForAPIBackend('annotationsAppApi')}/reporting`,
          {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
              ...(await getDefaultHeaders()).headers,
            },
          }
        );
      } else {
        console.log('Debounced Error Notification Payload:', payload);
      }

      // await post('/notifications', payload, z.any().parse);
    } catch (e) {
      console.error('Failed to send error notification:', e);
    }
  }

  // Clear the pending errors map after processing, but keep reportedErrorKeys to prevent future duplicates.
  allServiceErrorsMap.clear();
}, DEBOUNCE_TIME_MS);

/**
 * Reports an API error, accumulating occurrences and triggering a debounced notification.
 * @param url The URL of the failed API request.
 * @param status The HTTP status code of the response.
 * @param message An optional error message.
 */
export async function reportError(
  serviceName: string,
  url: string,
  status: number,
  message?: string
): Promise<void> {
  const userData = await user();

  if (!allServiceErrorsMap.has(serviceName)) {
    allServiceErrorsMap.set(serviceName, new Map<string, ErrorEntry>());
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const errorsMap = allServiceErrorsMap.get(serviceName)!;
  const key = `${url}-${status}`;
  const errorKey = `${serviceName}-${url}-${status}`;

  // If this error has already been reported in the current cycle, just increment the count.
  // This prevents resetting the debounce timer for already-known errors.
  if (reportedErrorKeys.has(errorKey)) {
    const existingError = errorsMap.get(key);
    if (existingError) existingError.count++;
    return;
  }

  const existingError = errorsMap.get(key);
  if (existingError) {
    existingError.count++;
    // Capture the first message if available and no message was previously set
    if (message && !existingError.message) {
      existingError.message = message;
    }
  } else {
    errorsMap.set(key, { url, status, message, count: 1 });
  }
  // Trigger the debounced processing function.
  void processAllServiceErrors(userData);
}

/**
 * Clears all accumulated errors for a specific service.
 * This should be called when a page/service is unmounted or its context changes.
 * @param serviceName The name of the service/page to clear errors for.
 */
export function clearErrorsForService(serviceName: string): void {
  allServiceErrorsMap.delete(serviceName);
}

/**
 * A higher-order function that wraps an async function with error handling logic.
 * Catches errors and reports them using the reportError function.
 * @param serviceName The name of the service/page reporting the error.
 * @param fn The async function to wrap.
 * @returns The result of the wrapped function.
 */
export async function withErrorHandling<T>(
  serviceName: string,
  fn: () => Promise<T>
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (error instanceof ResponseNotOk) {
      await reportError(
        serviceName,
        error.response.url,
        error.response.status,
        error.message
      );
    } else if (error instanceof Error) {
      await reportError(serviceName, 'unknown-api-url', 0, error.message);
    } else {
      await reportError(
        serviceName,
        'unknown-api-url',
        0,
        'An unknown error occurred during API call.'
      );
    }
    throw error;
  }
}
